import { playSample } from './audio'
import { GAME_START_SFX, GOAL_SUCCESS_SFX } from './missionSfx'
import { pickRandom } from './primitives/pickRandom'
import { snapshotAndConceal } from './primitives/serverStorageTemplate'
import { nextTempId } from './sharedHelpers'
import { BehaviorContext, HudStyle } from './types'

/**
 * RJF05 のマト当てゲーム（Lua: ServerScriptService/CreateMatoScript +
 * MatoScript + StarterGui/ScreenGui/GUILocalScript）。rjf05.ts が 800 行を
 * 超えたため、ミッション内の独立したギミックとしてファイル分割した
 * （挙動は分割前と同一。近似・意図的な差異は runCreateMato の doc を参照）。
 */

/**
 * マト（大砲の的）のオブジェクト名の接頭辞。rbxl では ServerStorage の
 * 「③マト」（緑のボール 20x20x20）。clone（名前 '③マト#2' 等）も対象に
 * するため前方一致で判定する。
 */
export const MATO_NAME = '③マト'

/** 初期表示の文言（実機 TextLabel の初期 Text。プレイ開始時から出ている）。 */
const MATO_INITIAL_TEXT = 'まもなくはじまります・・・'

/** スタート表示の文言（実機 StarterGui/ScreenGui/GUILocalScript）。 */
const MATO_START_TEXT = 'スタート！！！'

/** 生成ループ終了から終了表示までの待機秒数（Lua: 末尾の wait(5)）。 */
const MATO_FINISH_DELAY_SECONDS = 5

/** マト生成開始までの待機秒数（Lua: CreateMatoScript の wait(15)）。 */
const MATO_SETUP_DELAY_SECONDS = 15

/** 1 体目を出すまでの追加待機（Lua: StartEvent 発火後の wait(3)）。 */
const MATO_START_EXTRA_DELAY_SECONDS = 3

/** マトの生成間隔（Lua: wait(5)）。 */
const MATO_SPAWN_INTERVAL_SECONDS = 5

/** マトの生成数（Lua: for i = 1,20）。 */
const MATO_SPAWN_COUNT = 20

/** マトの一辺の候補（Lua: rand:NextInteger(1,3) * 20）。 */
const MATO_SCALES: ReadonlyArray<number> = [20, 40, 60]

/** マトの寿命（Lua: MatoScript の wait(15) → block:Destroy()）。 */
const MATO_LIFETIME_SECONDS = 15

/**
 * マトの一辺 → 命中スコア（Lua: MatoScript。小さいほど高得点）。
 * 20/40/60 以外（学習者がプログラムで寸法を変えた場合等）は実機同様 0 点。
 */
export const MATO_SCORE_BY_SIZE: ReadonlyMap<number, number> = new Map([
  [20, 50],
  [40, 30],
  [60, 10],
])

/**
 * マトの生成（Lua: ServerScriptService/CreateMatoScript）。
 * 開始 15 + 3 秒後から 5 秒ごとに、ServerStorage の ③マト と同じ見た目の
 * クローンを一辺 20/40/60 のランダム寸法で 20 体生成する。クローンには
 * 学習者が ③マト に付けたプログラムを起動する（Roblox の Clone() が配下の
 * Script ごと複製されるのと同じ）。生成位置はテンプレートと同じ
 * （Lua の Clone() は CFrame を引き継ぐ）。
 *
 * プレビューでの近似・意図的な差異:
 * - 開始音（スタート表示時）・終了音（生成完了時）は実音源（public/sounds/）を
 *   鳴らす（missionSfx.ts に定義）。開始/終了 GUI は HUD テキストで再現する。
 * - シーンに最初から置いてある ③マト（ServerStorage テンプレートの代役）は
 *   プログラムの結び付け先として World に必要だが、実機ではテンプレートは
 *   見えない（ServerStorage は描画されない）ため、プレイ中は非表示にする。
 *   クローンと同位置に重なって「マトが出てこない」ように見える問題も防ぐ。
 *   表示は次のプレイ開始時の world.reset() で復元される。
 */
/**
 * runCreateMato とスコア HUD の連携。実機は leaderstats が常時見えるが、
 * プレビューの HUD は 1 枠なので、開始・終了の文言と競合しないゲーム本編
 * （スタート文言が消えてから終了表示まで）に限ってスコアを表示する。
 */
export interface MatoScoreboard {
  /** 現在スコア（実機 leaderstats「スコア」）。終了表示で使う。 */
  readonly getScore: () => number
  /** ゲーム本編開始（スタート文言が消える時点）。スコア HUD の表示を始める。 */
  readonly onGameStart: (ctx: BehaviorContext) => void
  /** 終了表示の直前。以降の命中ではスコア HUD を更新しない。 */
  readonly onGameEnd: () => void
}

export const runCreateMato = async (
  ctx: BehaviorContext,
  scoreboard: MatoScoreboard,
): Promise<void> => {
  const { world } = ctx
  // クローンの複製元はこの時点のテンプレート状態（実機の ServerStorage に
  // 保存された状態に相当）。テンプレート自身に付いた学習者プログラムが
  // 後から動かしても、クローンには影響しない。
  // 実機の ServerStorage 同様、テンプレート本体はプレイ中見えない・撃てない・
  // 当たらない・触れない（hitMatoIfTouching は visible でないマトを対象外にする）。
  const snapshot = snapshotAndConceal(world, [MATO_NAME]).get(MATO_NAME)
  if (!snapshot) {
    return
  }
  // 実機はプレイ開始時から「まもなくはじまります・・・」が出ている。
  ctx.setHudText?.(MATO_INITIAL_TEXT, MATO_HUD_STYLE)
  // Lua: CreateMatoScript は wait(15) → StartEvent（GUI「スタート！！！」、
  // 3 秒後に非表示）→ wait(3) → マト生成開始。
  await ctx.wait(MATO_SETUP_DELAY_SECONDS)
  if (ctx.signal.aborted) {
    return
  }
  ctx.setHudText?.(MATO_START_TEXT, MATO_HUD_STYLE)
  // 実機の StartEvent（GUI「スタート！！！」）に合わせて開始音を鳴らす。
  playSample(GAME_START_SFX.url, GAME_START_SFX.volume, ctx.signal)
  await ctx.wait(MATO_START_EXTRA_DELAY_SECONDS)
  if (ctx.signal.aborted) {
    return
  }
  // スタート文言を下げ、ゲーム本編のスコア表示（実機 leaderstats 相当）へ
  // 切り替える。以降の命中ではスコアボード側が HUD を更新する。
  scoreboard.onGameStart(ctx)
  for (let index = 0; index < MATO_SPAWN_COUNT; index += 1) {
    if (ctx.signal.aborted) {
      return
    }
    // Lua: target:Clone()。ServerStorage 相当のスナップショットを複製する。
    const scale = pickRandom(MATO_SCALES)
    const id = nextTempId(world, 'rjf05_mato')
    world.addObject({
      id,
      name: snapshot.name,
      position: { ...snapshot.position },
      size: { x: scale, y: scale, z: scale },
      color: snapshot.color,
      material: snapshot.material,
      rotationY: snapshot.rotationY,
      orientation: snapshot.orientation,
      transparency: snapshot.transparency,
      canCollide: snapshot.canCollide,
      canTouch: snapshot.canTouch,
      isStatic: true,
      shape: snapshot.shape,
    })
    // 学習者が ③マト に付けたプログラムをクローンでも実行する。
    ctx.startObjectPrograms?.(id, MATO_NAME)
    // Lua: クローン配下の MatoScript は wait(15) → block:Destroy()。
    // 撃ち落とされなかったマトも 15 秒で自然に消える（fire-and-forget。
    // ctx.wait は中断時 resolve するため、abort 後は destroy しない）。
    void (async () => {
      await ctx.wait(MATO_LIFETIME_SECONDS)
      if (!ctx.signal.aborted && world.exists(id)) {
        world.destroy(id)
      }
    })()
    await ctx.wait(MATO_SPAWN_INTERVAL_SECONDS)
  }
  // Lua: 生成ループ後 wait(5) → EndEvent（GUI「ゲームしゅうりょう！スコアは
  // ◯だったよ！」を表示。実機はスコアを leaderstats から読む）。
  await ctx.wait(MATO_FINISH_DELAY_SECONDS)
  if (!ctx.signal.aborted) {
    // 終了表示が出たら、飛行中の弾の遅れた命中でスコア HUD が終了文言を
    // 上書きしないようにする（実機も終了文言は EndEvent 時点で固定される）。
    scoreboard.onGameEnd()
    // 実機の EndEvent（生成完了）に合わせて終了音を鳴らす。
    playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
    ctx.setHudText?.(
      `ゲームしゅうりょう！スコアは${scoreboard.getScore()}だったよ！`,
      MATO_HUD_STYLE,
    )
  }
}

/** ゲーム GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const MATO_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#000000',
  border: '#005500',
  borderWidth: 3,
  wide: true,
}
